import { useCallback, useState } from "react";
import { Node, NodeChange, NodePositionChange, useReactFlow } from "reactflow";

export type HelperLines = {
    horizontal?: number;
    vertical?: number;
    verticalStart?: number;
    verticalEnd?: number;
};

const SNAP_DISTANCE = 10;

// Get node dimensions from the DOM, accounting for zoom level
function getNodeDimensions(nodeId: string, zoom: number): { width: number; height: number } | null {
    const nodeElement = document.querySelector(`[data-id="${nodeId}"]`);
    if (!nodeElement) return null;

    const rect = nodeElement.getBoundingClientRect();
    // Divide by zoom to get flow coordinates instead of screen coordinates
    return { width: rect.width / zoom, height: rect.height / zoom };
}

// Helper function to get node positions and snap positions
function getHelperLines(
    change: NodePositionChange,
    nodes: Node[],
    zoom: number,
    distance = SNAP_DISTANCE
): { helperLines: HelperLines; snapPosition: { x: number; y: number } } {
    // Creates a default result with no guide lines
    const defaultResult = {
        helperLines: {
            horizontal: undefined,
            vertical: undefined,
            verticalStart: undefined,
            verticalEnd: undefined
        },
        snapPosition: change.position ? { x: change.position.x, y: change.position.y } : { x: 0, y: 0 }
    };

    // Early return if not dragging or no position data
    if (!change.dragging || !change.position) {
        return defaultResult;
    }

    // Finds the node being dragged
    const nodeA = nodes.find(node => node.id === change.id);
    // return if node not found
    if (!nodeA) {
        return defaultResult;
    }

    // Get dimensions from DOM
    const nodeADimensions = getNodeDimensions(nodeA.id, zoom);
    // return if dimensions not found
    if (!nodeADimensions) {
        return defaultResult;
    }

    // Calculates the bounds of the dragged node
    const nodeABounds = {
        left: change.position.x,
        right: change.position.x + nodeADimensions.width,
        top: change.position.y,
        bottom: change.position.y + nodeADimensions.height,
        centerX: change.position.x + nodeADimensions.width / 2,
        centerY: change.position.y + nodeADimensions.height / 2
    };

    let horizontalDistance = distance;
    let verticalDistance = distance;
    let snapX = change.position.x;
    let snapY = change.position.y;

    // Compare with All Other Nodes
    const result = nodes
        .filter(node => node.id !== nodeA.id)
        .reduce<HelperLines>((result, nodeB) => {
            // Get dimensions from DOM
            const nodeBDimensions = getNodeDimensions(nodeB.id, zoom);
            if (!nodeBDimensions) {
                return result;
            }

            // Calculates the bounds of the other nodes
            const nodeBBounds = {
                left: nodeB.position.x,
                right: nodeB.position.x + nodeBDimensions.width,
                top: nodeB.position.y,
                bottom: nodeB.position.y + nodeBDimensions.height,
                centerX: nodeB.position.x + nodeBDimensions.width / 2,
                centerY: nodeB.position.y + nodeBDimensions.height / 2
            };

            // Check for vertical alignment (left-right positioning)
            const distanceLeftLeft = Math.abs(nodeABounds.left - nodeBBounds.left);
            const distanceRightRight = Math.abs(nodeABounds.right - nodeBBounds.right);
            const distanceCenterCenter = Math.abs(nodeABounds.centerX - nodeBBounds.centerX);
            const distanceLeftRight = Math.abs(nodeABounds.left - nodeBBounds.right);
            const distanceRightLeft = Math.abs(nodeABounds.right - nodeBBounds.left);

            if (distanceLeftLeft < verticalDistance) {
                // if the distance between the left edges of the two nodes is less than the vertical distance
                // Show all three lines for the reference node
                result.verticalStart = nodeBBounds.left;
                result.vertical = nodeBBounds.centerX;
                result.verticalEnd = nodeBBounds.right;
                verticalDistance = distanceLeftLeft;
                snapX = nodeBBounds.left;
            } else if (distanceRightRight < verticalDistance) {
                // if the distance between the right edges of the two nodes is less than the vertical distance
                result.verticalStart = nodeBBounds.left;
                result.vertical = nodeBBounds.centerX;
                result.verticalEnd = nodeBBounds.right;
                verticalDistance = distanceRightRight;
                snapX = nodeBBounds.right - nodeADimensions.width;
            } else if (distanceCenterCenter < verticalDistance) {
                // if the distance between the center of the two nodes is less than the vertical distance
                result.verticalStart = nodeBBounds.left;
                result.vertical = nodeBBounds.centerX;
                result.verticalEnd = nodeBBounds.right;
                verticalDistance = distanceCenterCenter;
                snapX = nodeBBounds.centerX - nodeADimensions.width / 2;
            } else if (distanceLeftRight < verticalDistance) {
                // if the distance between the left edge of nodeA and the right edge of nodeB is less than the vertical distance
                result.verticalStart = nodeBBounds.left;
                result.vertical = nodeBBounds.centerX;
                result.verticalEnd = nodeBBounds.right;
                verticalDistance = distanceLeftRight;
                snapX = nodeBBounds.right;
            } else if (distanceRightLeft < verticalDistance) {
                // if the distance between the right edge of nodeA and the left edge of nodeB is less than the vertical distance
                result.verticalStart = nodeBBounds.left;
                result.vertical = nodeBBounds.centerX;
                result.verticalEnd = nodeBBounds.right;
                verticalDistance = distanceRightLeft;
                snapX = nodeBBounds.left - nodeADimensions.width;
            }

            // Check for horizontal alignment (top-bottom positioning)
            const distanceTopTop = Math.abs(nodeABounds.top - nodeBBounds.top);
            const distanceBottomBottom = Math.abs(nodeABounds.bottom - nodeBBounds.bottom);
            const distanceTopBottom = Math.abs(nodeABounds.top - nodeBBounds.bottom);
            const distanceBottomTop = Math.abs(nodeABounds.bottom - nodeBBounds.top);
            const distanceCenterCenterY = Math.abs(nodeABounds.centerY - nodeBBounds.centerY);

            // const CategoryLength = 30;
            // if (distanceTopTop < horizontalDistance) {
            //     // if the distance between the top edges of the two nodes is less than the horizontal distance
            //     result.horizontal = 30 + nodeBBounds.top;
            //     horizontalDistance = distanceTopTop;
            //     snapY = nodeBBounds.top;
            // } else if (distanceBottomBottom < horizontalDistance) {
            //     // if the distance between the bottom edges of the two nodes is less than the horizontal distance
            //     result.horizontal = 30 + nodeBBounds.bottom;
            //     horizontalDistance = distanceBottomBottom;
            //     snapY = nodeBBounds.bottom - nodeADimensions.height;
            // } else
            if (distanceCenterCenterY < horizontalDistance) {
                // if the distance between the center of the two nodes is less than the horizontal distance
                result.horizontal = nodeBBounds.centerY;
                horizontalDistance = distanceCenterCenterY;
                snapY = nodeBBounds.centerY - nodeADimensions.height / 2;
            }
            // else if (distanceTopBottom < horizontalDistance) {
            //     // if the distance between the top edge of nodeA and the bottom edge of nodeB is less than the horizontal distance
            //     result.horizontal = 30 + nodeBBounds.bottom;
            //     horizontalDistance = distanceTopBottom;
            //     snapY = nodeBBounds.bottom;
            // } else if (distanceBottomTop < horizontalDistance) {
            //     result.horizontal = 30 + nodeBBounds.top;
            //     horizontalDistance = distanceBottomTop;
            //     snapY = nodeBBounds.top - nodeADimensions.height;
            // }

            return result;
        }, defaultResult.helperLines);

    return {
        helperLines: result,
        snapPosition: { x: snapX, y: snapY }
    };
}

export function useHelperLines() {
    const { getNodes, getViewport } = useReactFlow();
    const [helperLines, setHelperLines] = useState<HelperLines>({
        horizontal: undefined,
        vertical: undefined,
        verticalStart: undefined,
        verticalEnd: undefined
    });

    const onNodeChangeWithHelperLines = useCallback(
        (changes: NodeChange[]): NodeChange[] => {
            const nodes = getNodes();
            const viewport = getViewport();

            // Process changes and apply snapping
            const updatedChanges = changes.map(change => {
                if (change.type === "position" && change.dragging && change.position) {
                    const result = getHelperLines(change, nodes, viewport.zoom);
                    setHelperLines(result.helperLines);

                    // Apply snapping to the position
                    return {
                        ...change,
                        position: result.snapPosition
                    };
                }
                return change;
            });

            // Clear helper lines when not dragging
            const isDragging = changes.some(change => change.type === "position" && change.dragging);
            if (!isDragging) {
                setHelperLines({
                    horizontal: undefined,
                    vertical: undefined,
                    verticalStart: undefined,
                    verticalEnd: undefined
                });
            }

            return updatedChanges;
        },
        [getNodes, getViewport]
    );

    return { helperLines, onNodeChangeWithHelperLines };
}
